import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap } from 'rxjs/operators';

import { PropertiesService } from '@app/services/properties.service';
import { selectSelectedPortfolioId } from '@dashboards/store/selectors/property-selector.selectors';
import { loadAllUserProperties } from '@main-application/store/actions/user.actions';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import { TurnoverConfigurationStepAssigneesService } from '../../services/turnover-configuration-step-assignees.service';
import {
  createResponsibleParties,
  createResponsiblePartiesFailure,
  createResponsiblePartiesSuccess,
  deletePortfolioResponsibleParties,
  deletePortfolioResponsiblePartiesFailure,
  deletePortfolioResponsiblePartiesSuccess,
  deletePropertyProjectManager,
  deleteResponsibleParties,
  loadDefaultPortfolioResponsibleParties,
  loadDefaultPortfolioResponsiblePartiesFailed,
  loadDefaultPortfolioResponsiblePartiesSuccess,
  loadResponsibleParties,
  loadResponsiblePartiesFailure,
  loadResponsiblePartiesSuccess,
  updatePortfolioResponsibleParties,
  updatePortfolioResponsiblePartiesFailure,
  updatePortfolioResponsiblePartiesSuccess,
  updatePropertyProjectManager,
  updatePropertyProjectManagerFailed,
  updatePropertyProjectManagerSuccess,
  updateResponsibleParties,
  updateResponsiblePartiesFailure,
  updateResponsiblePartiesSuccess,
} from '../actions/turnover-configuration-step-assignees.actions';

@Injectable()
export class TurnoverConfigurationStepAssigneesEffects {
  loadResponsibleParties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadResponsibleParties),
      switchMap(action =>
        this.responsiblePartiesService.getResponsiblePartiesOverrideByPortfolioId(action.portfolioId).pipe(
          map(responsibleParties => loadResponsiblePartiesSuccess({ responsibleParties: responsibleParties || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.WhileLoadingResponsibleParties);
            return of(loadResponsiblePartiesFailure());
          })
        )
      )
    )
  );

  createResponsibleParties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createResponsibleParties),
      mergeMap(action =>
        this.responsiblePartiesService.createResponsibleParties(action.responsibleParties).pipe(
          map(createResponsibleParties => createResponsiblePartiesSuccess({ createResponsibleParties })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.WhileCreatingAssignee);
            return this.store.select(selectSelectedPortfolioId).pipe(
              first(),
              mergeMap(portfolioId => of(createResponsiblePartiesFailure(), loadResponsibleParties({ portfolioId })))
            );
          })
        )
      )
    )
  );

  updateResponsibleParties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateResponsibleParties),
      mergeMap(action =>
        this.responsiblePartiesService.updateResponsibleParties(action.responsibleParties).pipe(
          map(updateResponsibleParties => updateResponsiblePartiesSuccess({ updateResponsibleParties })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.WhileUpdatingAssignee);
            return of(updateResponsiblePartiesFailure());
          })
        )
      )
    )
  );

  deleteResponsibleParties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteResponsibleParties),
      mergeMap(action =>
        this.responsiblePartiesService.deleteResponsibleParties(action.propertyId, action.workflowStepEnumType).pipe(
          map(updateResponsibleParties => {
            if (action.propertyName) {
              this.snackbarService.success(`${action.propertyName} deleted`);
            }
            return deletePortfolioResponsiblePartiesSuccess();
          }),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.WhileUpdatingAssignee);
            return of(deletePortfolioResponsiblePartiesFailure());
          })
        )
      )
    )
  );

  loadDefaultPortfolioResponsibleParties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDefaultPortfolioResponsibleParties),
      switchMap(action =>
        this.responsiblePartiesService.getDefaultAssigneesForPortfolio(action.portfolioId).pipe(
          map(responsibleParties =>
            loadDefaultPortfolioResponsiblePartiesSuccess({ responsibleParties: responsibleParties || [] })
          ),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.WhileLoadingResponsibleParties);
            return of(loadDefaultPortfolioResponsiblePartiesFailed());
          })
        )
      )
    )
  );

  updatePortfolioResponsibleParties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePortfolioResponsibleParties),
      mergeMap(action =>
        this.responsiblePartiesService
          .updateDefaultAssigneesForPortfolio(action.portfolioId, action.responsibleParties)
          .pipe(
            mergeMap(() => [
              loadDefaultPortfolioResponsibleParties({ portfolioId: action.portfolioId }),
              updatePortfolioResponsiblePartiesSuccess(),
            ]),
            catchError(() => {
              this.snackbarService.error(SnackbarErrorMessage.WhileUpdatingAssignee);
              return of(updatePortfolioResponsiblePartiesFailure());
            })
          )
      )
    )
  );

  deletePortfolioResponsibleParties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deletePortfolioResponsibleParties),
      mergeMap(action =>
        this.responsiblePartiesService
          .deleteDefaultAssigneesForPortfolio(action.portfolioId, action.workflowStepType)
          .pipe(
            mergeMap(() => [
              loadDefaultPortfolioResponsibleParties({ portfolioId: action.portfolioId }),
              deletePortfolioResponsiblePartiesSuccess(),
            ]),
            catchError(() => {
              this.snackbarService.error(SnackbarErrorMessage.WhileUpdatingAssignee);
              return of(deletePortfolioResponsiblePartiesFailure());
            })
          )
      )
    )
  );

  updatePropertyProjectManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePropertyProjectManager),
      mergeMap(action =>
        this.propertiesService.updatePropertyManagers([{ ...action }]).pipe(
          mergeMap(() => [updatePropertyProjectManagerSuccess()]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.WhileUpdatingAssignee);
            return of(updatePropertyProjectManagerFailed());
          })
        )
      )
    )
  );

  deletePropertyProjectManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deletePropertyProjectManager),
      mergeMap(action =>
        this.propertiesService.deletePropertyManagers([{ ...action }]).pipe(
          mergeMap(() => [updatePropertyProjectManagerSuccess(), loadAllUserProperties()]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.WhileUpdatingAssignee);
            return of(updatePropertyProjectManagerFailed());
          })
        )
      )
    )
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private snackbarService: SnackbarService,
    private propertiesService: PropertiesService,
    private responsiblePartiesService: TurnoverConfigurationStepAssigneesService
  ) {}
}
